import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Check, Trash2, UserRound, ClipboardList, CalendarDays } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApi, useResource, errMsg, notifyRefresh } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

export default function RemindersDialog({ open, onOpenChange, upcoming = [], onAppointment }) {
  const api = useApi();
  const { data: reminders, reload } = useResource("/reminders");
  const { data: patients } = useResource("/patients");
  const [form, setForm] = useState({ title: "", notes: "", due_date: "", patient_id: "" });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("tarefas");
  const list = Array.isArray(reminders) ? reminders : [];
  const pending = list.filter((r) => !r.completed);
  const completed = list.filter((r) => r.completed);
  const save = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 2) return toast.error("Informe o título do lembrete.");
    setSaving(true);
    try {
      await api.post("/reminders", { ...form, title: form.title.trim(), patient_id: form.patient_id || null });
      setForm({ title: "", notes: "", due_date: "", patient_id: "" });
      await reload();
      notifyRefresh();
      toast.success("Lembrete salvo");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const changeStatus = async (r) => {
    try {
      await api.patch(`/reminders/${r.id}/status`, { completed: !r.completed });
      await reload(); notifyRefresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Excluir este lembrete?")) return;
    try { await api.delete(`/reminders/${r.id}`); await reload(); notifyRefresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const card = (r) => (
    <div key={r.id} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3">
      <button type="button" onClick={() => changeStatus(r)} aria-label={r.completed ? "Reabrir lembrete" : "Concluir lembrete"}
        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 ${r.completed ? "border-emerald-500 bg-emerald-500 text-white" : "border-orange-400 hover:bg-orange-50"}`}>
        {r.completed && <Check className="h-3.5 w-3.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${r.completed ? "text-slate-400 line-through" : "text-slate-900"}`}>{r.title}</p>
        {r.notes && <p className="mt-1 whitespace-pre-wrap break-words text-xs text-slate-600">{r.notes}</p>}
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {r.due_date && <span className="rounded-full bg-amber-50 px-2 py-1 text-amber-800">Até {fmtDate(r.due_date)}</span>}
          {r.patient_id ? <Link onClick={() => onOpenChange(false)} to={`/pacientes/${r.patient_id}`} className="rounded-full bg-sky-50 px-2 py-1 font-medium text-sky-700 hover:underline"><UserRound className="mr-1 inline h-3 w-3" />{r.patient_name || "Abrir paciente"}</Link>
            : <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">Clínica</span>}
        </div>
      </div>
      <button type="button" onClick={() => remove(r)} aria-label="Excluir lembrete" className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="dashboard-reminders-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-xl"><BellRing className="h-5 w-5 text-orange-500" /> Central de Lembretes</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Organize tarefas da clínica e lembretes vinculados aos pacientes.</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setTab("tarefas")} className={`rounded-xl px-3 py-2 text-sm font-semibold ${tab === "tarefas" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600"}`}><ClipboardList className="mr-1 inline h-4 w-4" /> Anotações ({pending.length})</button>
          <button type="button" onClick={() => setTab("agenda")} className={`rounded-xl px-3 py-2 text-sm font-semibold ${tab === "agenda" ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600"}`}><CalendarDays className="mr-1 inline h-4 w-4" /> Consultas ({upcoming.length})</button>
        </div>
        {tab === "tarefas" ? <>
          <form onSubmit={save} className="space-y-3 rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
            <h3 className="text-sm font-bold text-slate-800">Novo lembrete</h3>
            <Input required maxLength={160} placeholder="Ex.: Ligar para paciente ou comprar materiais" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} aria-label="Título do lembrete" />
            <Textarea maxLength={2000} rows={2} placeholder="Detalhes ou observações (opcional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} aria-label="Observações" />
            <div className="grid gap-2 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-semibold text-slate-700" htmlFor="reminder-date">Data (opcional)</label><Input id="reminder-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><label className="mb-1 block text-xs font-semibold text-slate-700" htmlFor="reminder-patient">Vincular a paciente (opcional)</label>
                <select id="reminder-patient" value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  <option value="">Anotação geral da clínica</option>
                  {(Array.isArray(patients) ? patients : []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600">{saving ? "Salvando..." : "Salvar lembrete"}</Button>
          </form>
          <h3 className="text-sm font-bold text-slate-800">Pendentes ({pending.length})</h3>
          <div className="max-h-64 space-y-2 overflow-y-auto">{pending.length ? pending.map(card) : <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500">Nenhum lembrete pendente.</p>}</div>
          {completed.length > 0 && <details className="rounded-xl border border-slate-200 p-3"><summary className="cursor-pointer text-sm font-semibold text-slate-600">Concluídos ({completed.length})</summary><div className="mt-3 space-y-2">{completed.map(card)}</div></details>}
        </> : <>
          <p className="text-xs text-slate-500">Consultas futuras sincronizadas com a agenda. O envio automático de WhatsApp ainda não está ativo.</p>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {upcoming.length === 0 && <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Nenhuma consulta futura encontrada.</p>}
            {upcoming.map((a) => <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
              <div><p className="text-sm font-semibold text-slate-900">{a.patient_name || "Paciente"}</p><p className="text-xs text-slate-600">{fmtDate(a.date)} às {a.time} · {a.procedure || "Consulta"}</p></div>
              <Button type="button" size="sm" variant="outline" onClick={() => { onOpenChange(false); onAppointment(a); }}>Ver consulta</Button>
            </div>)}
          </div>
          <Link to="/agenda" onClick={() => onOpenChange(false)} className="text-center text-sm font-semibold text-sky-700 hover:underline">Abrir agenda completa</Link>
        </>}
      </DialogContent>
    </Dialog>
  );
}
